export type BalanceSheetAccount = {
  id: string;
  name: string;
  officialName?: string | null;
  mask?: string | null;
  type: "depository" | "investment" | "credit" | "loan" | "other";
  balance: number | string | null;
  institutionName?: string | null;
  balanceUpdatedAt?: Date | string | null;
  excludeFromNetWorthAndFire?: boolean;
};

export type BalanceSheetAsset = {
  id: string;
  name: string;
  assetType: "home" | "vehicle" | "rental_property" | "other";
  estimatedValue: number | null;
  valuationUpdatedAt?: Date | string | null;
  manualLiabilities: Array<{
    currentBalance: number;
  }>;
};

export type BalanceSheetLiability = {
  accountId: string;
  liabilityType: "mortgage" | "student";
};

export type BalanceSheetStatementLine = {
  id: string;
  label: string;
  detail: string | null;
  amount: number;
};

export type BalanceSheetStatementSection = {
  key: string;
  title: string;
  lines: BalanceSheetStatementLine[];
  total: number;
};

export type BalanceSheetSnapshot = {
  assetSections: BalanceSheetStatementSection[];
  liabilitySections: BalanceSheetStatementSection[];
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  excludedAccountCount: number;
};

function toAmount(value: number | string | null | undefined): number {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) ? amount : 0;
}

function accountDetail(account: BalanceSheetAccount): string | null {
  return (
    [account.institutionName, account.mask ? `••••${account.mask}` : null]
      .filter((value): value is string => Boolean(value))
      .join(" · ") || null
  );
}

function accountLine(account: BalanceSheetAccount): BalanceSheetStatementLine {
  return {
    id: `account-${account.id}`,
    label: account.officialName ?? account.name,
    detail: accountDetail(account),
    amount: toAmount(account.balance),
  };
}

function section(
  key: string,
  title: string,
  lines: BalanceSheetStatementLine[],
): BalanceSheetStatementSection | null {
  const nonZeroLines = lines
    .filter((line) => line.amount !== 0)
    .sort((a, b) => b.amount - a.amount);

  if (nonZeroLines.length === 0) return null;

  return {
    key,
    title,
    lines: nonZeroLines,
    total: nonZeroLines.reduce((sum, line) => sum + line.amount, 0),
  };
}

export function buildBalanceSheetSnapshot({
  accounts,
  assets,
  liabilities,
  taxRefundOrOwe = 0,
}: {
  accounts: BalanceSheetAccount[];
  assets: BalanceSheetAsset[];
  liabilities: BalanceSheetLiability[];
  taxRefundOrOwe?: number;
}): BalanceSheetSnapshot {
  const includedAccounts = accounts.filter(
    (account) => !account.excludeFromNetWorthAndFire,
  );
  const liabilityTypeByAccountId = new Map(
    liabilities.map(
      (liability) => [liability.accountId, liability.liabilityType] as const,
    ),
  );

  const cashLines = includedAccounts
    .filter((account) => account.type === "depository")
    .map(accountLine);
  const investmentLines = includedAccounts
    .filter((account) => account.type === "investment")
    .map(accountLine);
  const otherFinancialAssetLines = includedAccounts
    .filter((account) => account.type === "other")
    .map(accountLine);
  if (taxRefundOrOwe > 0) {
    otherFinancialAssetLines.push({
      id: "estimated-tax-refund",
      label: "Estimated current-year tax refund",
      detail: "Projected",
      amount: taxRefundOrOwe,
    });
  }

  const propertyLines = assets.map((asset) => ({
    id: `asset-${asset.id}`,
    label: asset.name,
    detail:
      asset.assetType === "rental_property"
        ? "Rental property"
        : asset.assetType === "home"
          ? "Real estate"
          : asset.assetType === "vehicle"
            ? "Vehicle"
            : "Other asset",
    amount: toAmount(asset.estimatedValue),
  }));

  const creditLines = includedAccounts
    .filter((account) => account.type === "credit")
    .map(accountLine);
  const mortgageLines = includedAccounts
    .filter(
      (account) =>
        account.type === "loan" &&
        liabilityTypeByAccountId.get(account.id) === "mortgage",
    )
    .map(accountLine);
  const studentLoanLines = includedAccounts
    .filter(
      (account) =>
        account.type === "loan" &&
        liabilityTypeByAccountId.get(account.id) === "student",
    )
    .map(accountLine);
  const otherLiabilityLines = includedAccounts
    .filter(
      (account) =>
        account.type === "loan" && !liabilityTypeByAccountId.has(account.id),
    )
    .map(accountLine);

  for (const asset of assets) {
    asset.manualLiabilities.forEach((liability, index) => {
      otherLiabilityLines.push({
        id: `asset-${asset.id}-manual-liability-${index}`,
        label: `${asset.name} liability`,
        detail: "Manually tracked",
        amount: toAmount(liability.currentBalance),
      });
    });
  }
  if (taxRefundOrOwe < 0) {
    otherLiabilityLines.push({
      id: "estimated-taxes-due",
      label: "Estimated current-year taxes due",
      detail: "Projected",
      amount: Math.abs(taxRefundOrOwe),
    });
  }

  const assetSections = [
    section("cash", "Cash & cash equivalents", cashLines),
    section("investments", "Investments", investmentLines),
    section("property", "Property & other assets", propertyLines),
    section("other-assets", "Other financial assets", otherFinancialAssetLines),
  ].filter((value): value is BalanceSheetStatementSection => value != null);

  const liabilitySections = [
    section("mortgages", "Mortgages", mortgageLines),
    section("student-loans", "Student loans", studentLoanLines),
    section("credit", "Credit cards", creditLines),
    section("other-liabilities", "Other liabilities", otherLiabilityLines),
  ].filter((value): value is BalanceSheetStatementSection => value != null);

  const totalAssets = assetSections.reduce((sum, item) => sum + item.total, 0);
  const totalLiabilities = liabilitySections.reduce(
    (sum, item) => sum + item.total,
    0,
  );
  return {
    assetSections,
    liabilitySections,
    totalAssets,
    totalLiabilities,
    netWorth: totalAssets - totalLiabilities,
    excludedAccountCount: accounts.length - includedAccounts.length,
  };
}
